/**
 * @module app.um
 */

import { autoinject, observable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { TransportationService } from 'app/um/services/transportation-service';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberService } from 'app/cd/services/member-service';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

type LocationOption = 'm' | 'p' | 'o' | null;

@autoinject
export class SaveTransportationAppointment extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'loadAppointment' })
    public appointment: UM.AuthTransportationAppointment | null = null;

    @bindable.stringNull
    public authId: string | null = null;

    @bindable.stringNull({ changeHandler: 'loadMember' })
    public memberId: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public showActions: boolean = false;

    protected isNewAppointment: boolean = true;
    protected daysOfWeek: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    protected member: CD.Member | null = null;
    private memberAddress: CD.MemberAddress | null = null;

    protected pickUpOption: LocationOption = null;
    @observable
    protected pickUpProviderLocation: CD.ProviderLocation | null = null;
    private pickUpFacilityAddress: CD.ProviderFacilityAddress | null = null;
    protected pickUpCustomAddress: string | null = null;
    protected showPickUpProviderSelect: boolean = true;

    protected dropOffOption: LocationOption = null;
    @observable
    protected dropOffProviderLocation: CD.ProviderLocation | null = null;
    private dropOffFacilityAddress: CD.ProviderFacilityAddress | null = null;
    protected dropOffCustomAddress: string | null = null;
    protected showDropOffProviderSelect: boolean = true;

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: TransportationService,
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.authId) { throw new Error('Bindable property [auth-id] was not set'); }
        if(!this.memberId) { throw new Error('Bindable property [member-id] was not set'); }

        this.loadMember().then(() => {
            this.loadAppointment(this.appointment || null);
        });
    }

    ////****************** Events ***************************************************////
    protected pickUpProviderLocationChanged() {
        const facilityAddress = this.pickUpProviderLocation?.facility?.address?.physical || null;
        this.pickUpFacilityAddress = facilityAddress;

        if(this.pickUpOption === 'p') {
            this.pickUpCustomAddress = facilityAddress?.$display || null;
            if(this.appointment?.pickUp) {
                this.appointment.pickUp.address = facilityAddress;
            }
        }
    }

    protected dropOffProviderLocationChanged() {
        const facilityAddress = this.dropOffProviderLocation?.facility?.address?.physical || null;
        this.dropOffFacilityAddress = facilityAddress;

        if(this.dropOffOption === 'p') {
            this.dropOffCustomAddress = facilityAddress?.$display || null;
        }
    }

    protected changePickUpOption(option: LocationOption): void {
        if(!this.appointment) { return; }
        if(this.pickUpOption !== option) { this.pickUpOption = option; }
        switch(this.pickUpOption) {
            case 'm':
                if(this.memberAddress) {
                    this.pickUpCustomAddress = this.memberAddress.$display!;
                    if(this.appointment.pickUp) {
                        this.appointment.pickUp.address = this.memberAddress;
                    }
                }
                return;
            case 'p':
                this.showPickUpProviderSelect = !this.pickUpFacilityAddress || !!this.pickUpProviderLocation;
                this.pickUpCustomAddress = this.pickUpFacilityAddress?.$display || null;
                return;
            case 'o':
                this.pickUpCustomAddress = null;
                return;
            default:
                this.pickUpCustomAddress = null;
                return;
        }
    }

    protected changeDropOffOption(option: LocationOption): void {
        if(!this.appointment) { return; }
        if(this.dropOffOption !== option) { this.dropOffOption = option; }
        switch(this.dropOffOption) {
            case 'm':
                if(this.memberAddress) {
                    this.dropOffCustomAddress = this.memberAddress.$display!;
                }
                return;
            case 'p':
                this.showDropOffProviderSelect = !this.dropOffFacilityAddress || !!this.dropOffProviderLocation;
                this.dropOffCustomAddress = this.dropOffFacilityAddress?.$display || null;
                return;
            case 'o':
                this.dropOffCustomAddress = null;
                return;
            default:
                this.dropOffCustomAddress = null;
                return;
        }
    }

    ////****************** Functions ************************************************////
    protected updateRecurrence(): void {
        if(!!this.appointment && (this.appointment.series || []).length === 0) {
            this.appointment.endDate = null;
        }
    }

    /** Loads an appointment into the form. If null, a new appointment is created */
    public loadAppointment(appointment: UM.AuthTransportationAppointment | null): void {
        if(this.appointment !== appointment) { this.appointment = appointment; }

        this.isNewAppointment = !(this.appointment && this.appointment.id);
        if(!this.appointment) {
            this.appointment = {
                auth: { id: this.authId }
            }
            return;  // Don't continue - the previous assignment will trigger the change handler again
        }

        if(!this.appointment.series) {
            this.appointment.series = [];
        }

        // Set Pick Up Option and Address
        this.setPickUpAddress();

        // Set Drop Up Option and Address
        this.setDropOffAddress();
    }

    /** Sets Pick Up Option and Address from appointment object */
    private setPickUpAddress(): void {
        const pickUp = this.appointment?.pickUp || null;
        if(pickUp?.address) {
            this.pickUpCustomAddress = pickUp.address.$display!;

            const pickUpAddressTypeCode = pickUp.address.attachedTo?.type?.code;
            if(pickUpAddressTypeCode === 'CD.Member') {
                this.pickUpOption = 'm';
            } else {
                this.pickUpOption = 'p';
                this.showPickUpProviderSelect = false;
            }
        } else if(pickUp?.customAddress) {
            this.pickUpCustomAddress = pickUp.customAddress || null;
            this.pickUpOption = 'o';
        } else {
            this.pickUpCustomAddress = null;
            this.pickUpOption = null;
        }
    }

    /** Sets Drop Off Option and Address from appointment object */
    private setDropOffAddress(): void {
        const dropOff = this.appointment?.dropOff || null;
        if(dropOff?.address) {
            this.dropOffCustomAddress = dropOff.address.$display!;

            const dropOffAddressTypeCode = dropOff.address.attachedTo?.type?.code;
            if(dropOffAddressTypeCode === 'CD.Member') {
                this.dropOffOption = 'm';
            } else {
                this.dropOffOption = 'p';
                this.showDropOffProviderSelect = false;
            }
        } else if(dropOff?.customAddress) {
            this.dropOffCustomAddress = dropOff.customAddress || null;
            this.dropOffOption = 'o';
        } else {
            this.dropOffCustomAddress = null;
            this.dropOffOption = null;
        }
    }

    /**
     * Gets an the correct address reference based on the currently selected Pick Up option.
     *
     * Used when building the save object
     */
    private getPickUpAddressReference(): CD.MemberAddress | CD.ProviderFacilityAddress | null {
        const address = this.appointment?.pickUp?.address || null;

        switch(this.pickUpOption) {
            case 'm':
                if(this.memberAddress) {
                    return getReference(this.memberAddress || address) || null;
                }
                return null;
            case 'p':
                if(this.pickUpFacilityAddress) {
                    return getReference(this.pickUpFacilityAddress || address) || null;
                }
                return null;
            default:
                return null;
        }
    }

    /**
     * Gets an the correct address reference based on the currently selected Drop Off option.
     *
     * Used when building the save object
     */
    private getDropOffAddressReference(): CD.MemberAddress | CD.ProviderFacilityAddress | undefined {
        const address = this.appointment?.dropOff?.address || null;

        switch(this.dropOffOption) {
            case 'm':
                if(this.memberAddress) {
                    return getReference(this.memberAddress || address) || undefined;
                }
                return undefined;
            case 'p':
                if(this.dropOffFacilityAddress) {
                    return getReference(this.dropOffFacilityAddress || address) || undefined;
                }
                return undefined;
            default:
                return undefined;
        }
    }

    /** Gathers input values, constructs the save object, and returns it */
    private buildSaveObject(): UM.AuthTransportationAppointment | null {
        if(!this.appointment) { return null; }

        if(this.appointment.comments === '') { this.appointment.comments = null; }

        const appt: UM.AuthTransportationAppointment = {
            id: this.appointment.id || undefined,
            auth: getReference(this.appointment.auth) || undefined,
            dateTime: this.appointment.dateTime || undefined,
            series: this.appointment.series || undefined,
            endDate: this.appointment.endDate || null,
            comments: this.appointment.comments !== undefined ? this.appointment.comments : undefined,
            isWillCall: this.appointment.isWillCall || false,
            pickUp: {
                dateTime: (this.appointment.pickUp && this.appointment.pickUp.dateTime) || undefined,
                isWillCall: (this.appointment.pickUp && this.appointment.pickUp.isWillCall) || false,
                customAddress: (this.pickUpCustomAddress) || undefined,
                address: this.getPickUpAddressReference() || null
            },
            dropOff: {
                customAddress: (this.dropOffCustomAddress) || undefined,
                address: this.getDropOffAddressReference() || null
            },
            return: {
                dateTime: (this.appointment.return && this.appointment.return.dateTime) || undefined,
                isWillCall: (this.appointment.return && this.appointment.return.isWillCall) || false,
            }
        }

        return appt;
    }

    //Get the "Date" only portion of a DateTime value
    private getDate(strDateTime: string): string | null {
        if(!strDateTime) { return null; }

        const dateTime: DateTimeValue = new DateTimeValue(strDateTime);
        if(!!!dateTime) { return null; }

        return dateTime.toDate(this.env.displayTimeZone).toString() || null;
    }

    ////****************** IForm ****************************************************////
    public reset(): void {
        this.appointment = null;
        this.pickUpOption = null;
        this.pickUpCustomAddress = null;
        this.pickUpProviderLocation = null;
        this.pickUpFacilityAddress = null;

        this.dropOffOption = null;
        this.dropOffCustomAddress = null;
        this.dropOffProviderLocation = null;
        this.dropOffFacilityAddress = null;
    }

    public hasChanges(): boolean {
        return false;
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        return await this.saveAppointment();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;

        const includes = [
            'address.home'
        ];

        const fields = [
            ...memberRequiredFields,
            'address.home.city',
            'address.home.postalCode',
            'address.home.region.code',
            'address.home.street1',
            'address.home.street2'
        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMember(this.memberId, includes, fields);
            this.member = response.results[0];

            if(this.member.address && this.member.address.home) {
                this.memberAddress = this.member.address.home;
                if(this.pickUpOption === 'm') {
                    this.pickUpCustomAddress = this.memberAddress.$display!;
                }
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveAppointment(): Promise<boolean> {
        try {
            const appt = this.buildSaveObject();
            if(!appt) { throw new Error('Unable to build save object'); }

            this.state.beginSave();
            const response = await this.service.saveAppointment(appt);
            if(response.isSuccessful) { this.reset(); }
            return response.isSuccessful;
        } catch(error) {
            this.toaster.standard.saved('um:auth.transportationAppointment.$', false);
            this.logger.error('Error saving auth transportation appointment object');
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
